/**
 * Which module is open, what state it is in, and what can be done with it.
 *
 * @module components/studio/ModuleEditorHeader
 */

import React from "react";

import { creditsLabel } from "@/components/shared/credits";
import { recordVersionLabel } from "@/components/shared/revision-labels";
import { WorkflowStateBadge } from "@/components/shared/WorkflowStateBadge";
import { readOnlyReason } from "@/components/studio/ModuleNotices";
import { ModuleSaveStatus } from "@/components/studio/ModuleSaveStatus";
import type { ModuleEditorState } from "@/components/studio/useModuleEditor";
import { Badge, Button, Heading, InfoAlert } from "@/components/ui";

/** Submitting freezes the module, so it waits on the same lease a save does. */
function submitRefusal(editor: ModuleEditorState): string {
  if (!editor.canEdit) {
    return readOnlyReason(editor);
  }
  return "Another review action is running";
}

export function ModuleEditorHeader({
  editor,
  onSubmitPressed,
  inChangeCase = false,
}: {
  editor: ModuleEditorState;
  onSubmitPressed: () => void;
  /** A change case is reviewed whole, so its modules carry no review status. */
  inChangeCase?: boolean;
}): React.ReactElement | null {
  const { record, moduleDraft } = editor;
  if (!record || !moduleDraft) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="min-w-0">
        <Heading level={2} id="module-editor-heading">
          {moduleDraft.code ? `${moduleDraft.code} — ` : ""}
          {moduleDraft.title || "Untitled module"}
        </Heading>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <WorkflowStateBadge state={record.workflowState} />
          {/* Drawn only where there is a value, as the module list beside it
              is: "0 credits" reads as a decision somebody made, and zero is
              what Add module stores. */}
          {!moduleDraft.credits ? null : (
            <Badge tone="neutral">{creditsLabel(moduleDraft.credits)}</Badge>
          )}
          <Badge tone="neutral">{record.ownerName ?? "No builder assigned"}</Badge>
          <Badge tone={editor.canEdit ? "success" : "neutral"} variant="soft">
            {editor.canEdit ? "Editable" : "Read only"}
          </Badge>
          <Badge tone="neutral">{recordVersionLabel(record.programmeRevision)}</Badge>
          {inChangeCase ? null : (
            <Badge tone="primary" variant="soft" data-testid="module-review-status">
              {record.moduleReview.status}
            </Badge>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ModuleSaveStatus
          status={editor.saveStatus}
          dirty={editor.dirty}
          canEdit={editor.canEdit}
        />
        {record.canSubmit ? (
          <Button
            variant="primary"
            icon="paper-plane-tilt"
            loading={editor.reviewAction === "submit"}
            disabled={!editor.canEdit || editor.reviewAction !== null}
            disabledReason={submitRefusal(editor)}
            onClick={onSubmitPressed}
            data-testid="module-submit-for-review"
          >
            Submit for review
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * A case's modules are not submitted one by one, so without this a builder
 * sees Save and nothing that says where the work goes next.
 */
export function ChangeCaseHandOff({
  leadName = undefined,
  readerLeads = false,
}: {
  /** Who submits the change case. */
  leadName?: string;
  /** The reader is that person. */
  readerLeads?: boolean;
}): React.ReactElement {
  return (
    <InfoAlert data-testid="module-change-case-hand-off">
      {readerLeads
        ? "Edits save into this change case. You submit the whole case for review once the team's work is in."
        : `Your edits save into this change case. ${leadName ?? "The programme lead"} submits the whole case for review, so there is nothing to submit from this module.`}
    </InfoAlert>
  );
}

export default ModuleEditorHeader;
